import React from "react";
import {observer} from "mobx-react-lite";
import {useSitStores} from "../../../stores";
import {Autocomplete, Button, Checkbox, FormControl, IconButton, InputAdornment, MenuItem, Select, TextField} from "@mui/material";
import {Add, Apartment, Category as CategoryIcon, Clear, Search} from "@mui/icons-material";
import {generatePath, useNavigate} from "react-router";
import {sitRoutes} from "../../routing/sitRoutes";
import type {Category, Company} from "../../ApiTypes";
import {category_readable_str} from "../../ApiTypes";
import {sitCountTags} from "../../components/design/SitDesignAtoms";

// Prepend a leading icon to an Autocomplete's input, keeping its own adornment (tags).
const withIcon = (icon: React.ReactNode, params: React.ComponentProps<typeof TextField>['InputProps']) => ({
    ...params,
    startAdornment: <><InputAdornment position="start">{icon}</InputAdornment>{params?.startAdornment}</>,
});

export const TargetFilters: React.FC = observer(() => {
    const {targetListStore: store, ccStore} = useSitStores();
    const navigate = useNavigate();

    return (
        <div className="target-filters-component sit-filterbar">
            <TextField
                className="sit-search-field"
                size="small"
                placeholder="Search targets"
                value={store.search}
                onChange={e => store.setSearch(e.target.value)}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><Search fontSize="small"/></InputAdornment>,
                    endAdornment: store.search
                        ? <InputAdornment position="end">
                            <IconButton size="small" aria-label="clear search" onClick={() => store.setSearch('')}>
                                <Clear fontSize="small"/>
                            </IconButton>
                        </InputAdornment>
                        : undefined,
                }}/>

            <FormControl size="small" className="sitf sit-filter-select">
                <span className="sitf-label">View</span>
                <Select value={store.my_targets}
                        onChange={(event) => store.setMyTargets(Number(event.target.value))}>
                    <MenuItem value={0}>All targets</MenuItem>
                    <MenuItem value={1}>My targets</MenuItem>
                </Select>
            </FormControl>

            <label className="sitf sit-filter-auto">
                <span className="sitf-label">Category</span>
                <Autocomplete
                    multiple fullWidth selectOnFocus clearOnBlur handleHomeEndKeys disableCloseOnSelect
                    size="small" limitTags={1} renderTags={sitCountTags}
                    options={ccStore.categoryRM.result ?? ([] as Category[])}
                    getOptionLabel={(option) => category_readable_str(option)}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    value={store.categories}
                    renderInput={(params) =>
                        <TextField {...params} placeholder="All"
                                   InputProps={withIcon(<CategoryIcon fontSize="small"/>, params.InputProps)}/>}
                    renderOption={(props, option) =>
                        <li {...props} key={option.id}>
                            <Checkbox checked={store.categories.find((c) => c.id === option.id) !== undefined}/>
                            {category_readable_str(option)}
                        </li>}
                    onChange={(_event, value) => store.setCategories(value)}/>
            </label>

            <label className="sitf sit-filter-auto">
                <span className="sitf-label">Company</span>
                <Autocomplete
                    multiple fullWidth selectOnFocus clearOnBlur handleHomeEndKeys disableCloseOnSelect
                    size="small" limitTags={1} renderTags={sitCountTags}
                    options={ccStore.companyRM.result ?? ([] as Company[])}
                    getOptionLabel={(option) => option.title}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    value={store.companies}
                    renderInput={(params) =>
                        <TextField {...params} placeholder="All"
                                   InputProps={withIcon(<Apartment fontSize="small"/>, params.InputProps)}/>}
                    renderOption={(props, option) =>
                        <li {...props} key={option.id}>
                            <Checkbox checked={store.companies.find((c) => c.id === option.id) !== undefined}/>
                            {option.title}
                        </li>}
                    onChange={(_event, value) => store.setCompanies(value)}/>
            </label>

            <span className="sit-filterbar-spacer"/>

            <Button variant="contained" className="sit-add-btn" startIcon={<Add/>}
                    onClick={() => navigate(generatePath(sitRoutes.sit_target, {targetId: 'new'}))}>
                Add new target
            </Button>
        </div>
    );
});
